<script setup>
import {onBeforeUnmount, onMounted, ref} from "vue";
import SvgButton from "@/components/common/svgButton.vue";
import {LogIn} from "@/composables/auth";
import {useStore} from "@/store";
import {personalAreaInitialize} from "@/composables/PersonalAreaInitialize";
import {t, useGlobusLocal} from "@/controllerLK/GlobusLocalization";
import LanguageSelector from "./LoginPage/LanguageSelector.vue";
import {goMainPage} from "@/composables/ModuleCommunication";
import {useProjects} from "@/store/projects";
import {useUser} from "@/store/user";
import axios from "axios";
import PasswordUnvisibleIcon from "@/assets/icons/PasswordUnvisibleIcon.vue";
import PasswordVisibleIcon from "@/assets/icons/PasswordVisibleIcon.vue";

const { initGlobusLocal } = useGlobusLocal();

const backendVersion = ref(null);


onMounted(() => {
  // Стили страницы авторизации должны быть стилями глобуса.
  document.getElementById("appMain").classList.add("login-theme");

  document.addEventListener("keydown", handleEnterClick);
  getAppVersion();
  getBackendVersion();
  initGlobusLocal(store.language.code, "personal_area");
});

onBeforeUnmount(() => {
  // Убираем класс loginTheme, для возврата стилей ЛК.
  document.getElementById("appMain").classList.remove("login-theme");
  document.removeEventListener("keydown", handleEnterClick);
});

const getAppVersion = async () => {
  while (LKVersion.value.length === 0 || APPVersion.value.length === 0) {
    LKVersion.value = localStorage.getItem("personalAreaVersion") ? localStorage.getItem("personalAreaVersion") : "";
    APPVersion.value = localStorage.getItem("appVersion") ? localStorage.getItem("appVersion") : "";
    await new Promise(r => setTimeout(r, 2000));
  }
};

const getBackendVersion = async () => {
  try {
    const response = await axios.get("/globus/api/Version");
    backendVersion.value = response.data;
  } catch (e) {
    console.log(e);
  }
};

const store = useStore();
const userStore = useUser();
const projectStore = useProjects();

const passwordVisible = ref(false);
const changePasswordVisible = () => {
  passwordVisible.value = !passwordVisible.value;
};

const userForm = ref({
  username: "",
  password: ""
});

const isSubmitted = ref(false);
const isUsernameError = ref();
const isPasswordError = ref();

const doLogin = async () => {
  isUsernameError.value = false;
  isPasswordError.value = false;
  isSubmitted.value = true;

  if (userForm.value.username.length === 0) {
    isUsernameError.value = true;
  }

  if (userForm.value.password.length === 0) {
    isPasswordError.value = true;
  }

  if (isUsernameError.value || isPasswordError.value) {
    store.createNotify(t("login_page.error.invalid"), "error");
    return false;
  }

  userForm.value.username = userForm.value.username.trim();
  userForm.value.password = userForm.value.password.trim();

  store.loader = true;

  let loginData = await LogIn(userForm.value);

  if (loginData.status) {
    await personalAreaInitialize();
    if (projectStore.projects.length > 0 || userStore.user.activeProject !== 0) {
      goMainPage();
    }

  } else {
    if (loginData.code === 401) {
      store.createNotify(t("login_page.error.invalid"), "error");
    } else {
      store.createNotify(t("login_page.error.server"), "error");
    }
  }
  store.loader = false;
  isSubmitted.value = false;
};


const LKVersion = ref("");
const APPVersion = ref("");

const handleEnterClick = (event) => {
  if (event.keyCode !== 13) {
    return false;
  } else {
    doLogin();
  }
};

</script>

<template>
  <div
    id="login-page"
    class="login-page"
  >
    <div class="login-box">
      <div class="login-box__header">
        <LanguageSelector />
      </div>
      <div class="login-box__body">
        <form
          class="login-form"
          @submit.prevent="doLogin"
        >
          <div class="login-form__title">
            {{ t('login_page.modules.' + store.activeModule.dataName) }}
            <SvgButton
              :key="store.activeModule.dataName"
              height="24px"
              width="24px"
              :name="store.activeModule.dataName"
            />
          </div>
          <div class="login-form__input">
            <InputPrimary
              id="user-text-field"
              v-model="userForm.username"
              :error="isUsernameError"
              :label="t('login_page.label.login')"
              name="username"
              autocomplete="username"
            />
            <InputPrimary
              id="password-text-field"
              v-model="userForm.password"
              :error="isPasswordError"
              :type="passwordVisible ? 'text' : 'password'"
              :label="t('login_page.label.password')"
              name="current-password"
              autocomplete="current-password"
            >
              <template
                #suffix
              >
                <PasswordVisibleIcon
                  v-if="passwordVisible"
                  class="icon"
                  @click="changePasswordVisible"
                />
                <PasswordUnvisibleIcon
                  v-else
                  class="icon"
                  @click="changePasswordVisible"
                />
              </template>
            </InputPrimary>
          </div>
          <ButtonAccentPrimary
            size="large"
            class="login-form__button"
            type="submit"
          >
            {{ t('login_page.button.accept') }}
          </ButtonAccentPrimary>
        </form>
      </div>
    </div>
    <div class="version">
      {{ t('login_page.label.version') }}: {{ LKVersion }} - {{ APPVersion }} - {{ backendVersion }}
    </div>
  </div>
</template>

<style scoped lang="scss">

.icon {
  width: 16px;
  height: 16px;
  cursor: pointer;
  user-select: none;
}

.version {
  font-size: 14px;
  font-weight: 400;
  color: #FFFFFF33;
}

.login-page {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background: #09518C;
  line-height: 1;
}

.login-box {
  display: flex;
  flex-direction: column;

  width: 580px;
  height: 480px;
  padding: 20px 30px;
  border-radius: 20px;
  background: #FFF;
  gap: 80px;

  font-size: 14px;
  font-weight: 400;

  &__header {
    display: flex;
    justify-content: flex-end;
  }

  &__body {
    display: flex;
    align-items: center;
    justify-content: center;

    .login-form {
      display: flex;
      flex-direction: column;
      width: 70%;
      gap: 28px;

      &__input {
        display: flex;
        flex-direction: column;
        gap: 28px;
      }

      &__title {
        font-size: 20px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        user-select: none;
        letter-spacing: 2.4px;
        text-transform: uppercase;
        color: #000;
        gap: 10px;
      }

      &__button {
        min-width: 110px;
        width: fit-content;
      }
    }
  }
}
</style>
